// components/common/Navbar.jsx — Ink & Lime Design System
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { STORAGE_KEYS } from '../../utils/constants';
import {
  UserIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const T = {
  ink:   '#0D0D0F',
  ink2:  '#1A1A20',
  slate: '#2D2D38',
  muted: '#6B6B7A',
  white: '#FFFFFF',
  lime:  '#C8F23C',
  limeD: '#A8D020',
};

const NavLink = ({ to, children, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <Link to={to} onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display:'inline-flex', alignItems:'center', gap:6,
        padding:'8px 16px', borderRadius:10,
        border:`1px solid ${hov?'rgba(255,255,255,.35)':'rgba(255,255,255,.15)'}`,
        background: hov ? 'rgba(255,255,255,.1)' : 'transparent',
        color:T.white, fontSize:13, fontWeight:600,
        textDecoration:'none', transition:'all .18s', whiteSpace:'nowrap',
      }}>
      {children}
    </Link>
  );
};

const PrimaryBtn = ({ to, children, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <Link to={to} onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display:'inline-flex', alignItems:'center', gap:6,
        padding:'8px 18px', borderRadius:10, border:'none',
        background: hov ? T.limeD : T.lime,
        color:T.ink, fontSize:13, fontWeight:800,
        textDecoration:'none', transition:'all .18s',
        transform: hov ? 'scale(1.03)' : 'scale(1)', whiteSpace:'nowrap',
      }}>
      {children}
    </Link>
  );
};

const Navbar = () => {
  const { user: contextUser, isAuthenticated, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [scrolled,    setScrolled]    = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    try {
      const s = localStorage.getItem(STORAGE_KEYS.USER);
      if (s) setCurrentUser(JSON.parse(s));
    } catch {}
  }, [contextUser, isAuthenticated]);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    try { await logout(); } catch {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
    }
    setCurrentUser(null); setMenuOpen(false); navigate('/');
  };

  const displayName = () => {
    const u = (currentUser || contextUser)?.data || currentUser || contextUser;
    if (!u) return 'Guest';
    if (u.name?.trim() && u.name !== 'User') return u.name.trim();
    if (u.phoneNumber) return u.phoneNumber;
    if (u.email) return u.email.split('@')[0];
    return 'User';
  };

  const user = currentUser || contextUser;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700;800&family=Space+Grotesk:wght@700&display=swap');
        .veg-nav * { box-sizing:border-box; font-family:'DM Sans',sans-serif; }
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes slideDown { from{opacity:0;transform:translateY(-8px);}to{opacity:1;transform:none;} }
        .veg-mobile-link {
          display:flex; align-items:center; gap:10px;
          padding:12px 16px; border-radius:12px;
          color:#fff; font-size:14px; font-weight:600;
          text-decoration:none; transition:background .18s;
          border:none; background:none; width:100%; cursor:pointer; font-family:'DM Sans',sans-serif;
        }
        .veg-mobile-link:hover { background:rgba(255,255,255,.08); }
        @media(max-width:768px){
          .veg-desktop { display:none !important; }
          .veg-burger  { display:flex !important; }
        }
        @media(min-width:769px){
          .veg-burger { display:none !important; }
        }
      `}</style>

      <nav className="veg-nav" style={{
        position:'sticky', top:0, zIndex:100,
        background: scrolled ? T.ink : 'rgba(13,13,15,.82)',
        backdropFilter: scrolled ? 'none' : 'blur(18px)',
        WebkitBackdropFilter: scrolled ? 'none' : 'blur(18px)',
        boxShadow: scrolled ? '0 4px 28px rgba(0,0,0,.55)' : 'none',
        borderBottom: `1px solid ${scrolled ? T.slate : 'rgba(255,255,255,.07)'}`,
        transition:'background .3s, box-shadow .3s, border-color .3s',
      }}>
        {/* Top lime accent bar */}
        <div style={{ height:2, background:T.lime, opacity: scrolled ? 1 : 0, transition:'opacity .3s' }}/>

        <div style={{ maxWidth:1400, margin:'0 auto', padding:'0 24px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', height:62 }}>

            {/* Logo */}
            <Link to="/" style={{ display:'flex', alignItems:'center', gap:10, textDecoration:'none' }}>
              <img src="/logo-vegatas.jpeg" alt="VEGATAS"
                style={{ height:56, width:'auto', objectFit:'contain', filter:'brightness(1.15)' }}/>
            </Link>

            {/* Desktop auth area */}
            <div className="veg-desktop" style={{ display:'flex', alignItems:'center', gap:8 }}>
              {loading ? (
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:18, height:18, borderRadius:'50%', border:'2px solid rgba(255,255,255,.15)', borderTopColor:T.lime, animation:'spin .7s linear infinite' }}/>
                  <span style={{ fontSize:13, color:'rgba(255,255,255,.4)' }}>Loading…</span>
                </div>
              ) : isAuthenticated && user ? (
                <>
                  <NavLink to="/profile">
                    <UserIcon style={{ width:15, height:15 }}/>
                    <span style={{ maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{displayName()}</span>
                  </NavLink>
                  <NavLink to="/rental/search/my-bookings">
                    <ClockIcon style={{ width:15, height:15 }}/>
                    My Bookings
                  </NavLink>
                  <button onClick={handleLogout}
                    onMouseEnter={e=>{ e.currentTarget.style.background='rgba(255,255,255,.1)'; e.currentTarget.style.borderColor='rgba(255,255,255,.35)'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background='transparent'; e.currentTarget.style.borderColor='rgba(255,255,255,.15)'; }}
                    style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10,
                      border:'1px solid rgba(255,255,255,.15)', background:'transparent',
                      color:T.white, fontSize:13, fontWeight:600, cursor:'pointer', transition:'all .18s' }}>
                    <ArrowRightOnRectangleIcon style={{ width:15, height:15 }}/>
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <NavLink to="/login">Login</NavLink>
                  <PrimaryBtn to="/register">Register →</PrimaryBtn>
                </>
              )}
            </div>

            {/* Hamburger */}
            <button className="veg-burger" onClick={() => setMenuOpen(o=>!o)} aria-label="Toggle menu"
              style={{ padding:8, borderRadius:10, border:'1px solid rgba(255,255,255,.15)',
                background: menuOpen ? 'rgba(255,255,255,.1)' : 'transparent',
                color:T.white, cursor:'pointer' }}>
              {menuOpen ? <XMarkIcon style={{ width:22, height:22 }}/> : <Bars3Icon style={{ width:22, height:22 }}/>}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        <div style={{
          overflow:'hidden', maxHeight: menuOpen ? 360 : 0,
          transition:'max-height .3s ease',
          borderTop: menuOpen ? `1px solid ${T.slate}` : 'none',
          background:T.ink2,
        }}>
          <div style={{ padding:'12px 16px 20px', display:'flex', flexDirection:'column', gap:4,
            animation: menuOpen ? 'slideDown .25s ease' : 'none' }}>
            {isAuthenticated && user ? (
              <>
                <Link to="/profile" className="veg-mobile-link" onClick={()=>setMenuOpen(false)}>
                  <UserIcon style={{ width:18, height:18, color:T.lime }}/>{displayName()}
                </Link>
                <Link to="/rental/search/my-bookings" className="veg-mobile-link" onClick={()=>setMenuOpen(false)}>
                  <ClockIcon style={{ width:18, height:18, color:T.lime }}/>My Bookings
                </Link>
                <button className="veg-mobile-link" onClick={handleLogout}>
                  <ArrowRightOnRectangleIcon style={{ width:18, height:18, color:T.muted }}/>Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="veg-mobile-link" onClick={()=>setMenuOpen(false)}>Login</Link>
                <Link to="/register" onClick={()=>setMenuOpen(false)}
                  style={{ display:'block', textAlign:'center', padding:'12px 16px', borderRadius:12,
                    background:T.lime, color:T.ink, fontSize:14, fontWeight:800, textDecoration:'none', marginTop:4 }}>
                  Register →
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
